import React from 'react';
import { XIcon, ClockIcon, UserIcon, PhoneIcon, MailIcon, ActivityIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import { Lead } from '../types';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { formatTimeAgo } from '../utils/time';
interface SidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  lead: Lead | null;
  eligibility: 'Eligible' | 'At Risk' | 'Will Be Cancelled';
}
export function SidePanel({
  isOpen,
  onClose,
  lead,
  eligibility
}: SidePanelProps) {
  if (!isOpen || !lead) return null;
  const isCancelled = eligibility === 'Will Be Cancelled';
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-900/20 backdrop-blur-sm z-40 transition-opacity" onClick={onClose} data-id="element-160" />

      {/* Panel */}
      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col border-l border-gray-200" data-id="element-161">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50" data-id="element-162">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-163">Lead Details</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" data-id="element-164">
            <XIcon className="w-5 h-5" data-id="element-165" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto" data-id="element-166">
          {/* Status Banner */}
          <div className={`px-6 py-4 border-b ${isCancelled ? 'bg-red-50 border-red-100' : 'bg-blue-50 border-blue-100'}`} data-id="element-167">
            <div className="flex items-start gap-3" data-id="element-168">
              {isCancelled ? <XCircleIcon className="w-5 h-5 text-red-600 mt-0.5" data-id="element-169" /> : <ClockIcon className="w-5 h-5 text-blue-600 mt-0.5" data-id="element-170" />}
              <div data-id="element-171">
                <h3 className={`text-sm font-medium ${isCancelled ? 'text-red-900' : 'text-blue-900'}`} data-id="element-172">
                  {isCancelled ? 'Will NOT be sent to Five9' : 'Pending Five9 Enrollment'}
                </h3>
                <p className={`text-sm mt-1 ${isCancelled ? 'text-red-700' : 'text-blue-700'}`} data-id="element-173">
                  {isCancelled ? `Lead status is currently "${lead.status}". Only New or Contact Attempted leads are eligible for auto-enrollment.` : 'This lead is in the 10-minute buffer window and will be automatically evaluated for sending.'}
                </p>
              </div>
            </div>
          </div>

          {/* Lead Info */}
          <div className="p-6 border-b border-gray-100" data-id="element-174">
            <div className="flex items-center justify-between mb-4" data-id="element-175">
              <h1 className="text-xl font-bold text-gray-900" data-id="element-176">{lead.name}</h1>
              <Badge variant={isCancelled ? 'danger' : 'success'} data-id="element-177">
                {lead.status}
              </Badge>
            </div>

            <div className="space-y-4" data-id="element-178">
              <div className="flex items-center text-sm text-gray-600" data-id="element-179">
                <PhoneIcon className="w-4 h-4 mr-3 text-gray-400" data-id="element-180" />
                {lead.phone}
              </div>
              <div className="flex items-center text-sm text-gray-600" data-id="element-181">
                <MailIcon className="w-4 h-4 mr-3 text-gray-400" data-id="element-182" />
                {lead.email}
              </div>
              <div className="flex items-center text-sm text-gray-600" data-id="element-183">
                <UserIcon className="w-4 h-4 mr-3 text-gray-400" data-id="element-184" />
                Owner:{' '}
                <span className="font-medium text-gray-900 ml-1" data-id="element-185">
                  {lead.owner}
                </span>
              </div>
              <div className="flex items-center text-sm text-gray-600" data-id="element-186">
                <ActivityIcon className="w-4 h-4 mr-3 text-gray-400" data-id="element-187" />
                Source:{' '}
                <span className="font-medium text-gray-900 ml-1" data-id="element-188">
                  {lead.source}
                </span>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="p-6" data-id="element-189">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wider" data-id="element-190">
              Activity Timeline
            </h3>
            <div className="space-y-6" data-id="element-191">
              {lead.activities.map((activity, index) => <div key={activity.id} className="relative flex gap-4" data-id="element-192">
                  {/* Line connecting items */}
                  {index !== lead.activities.length - 1 && <div className="absolute left-2.5 top-6 bottom-[-24px] w-px bg-gray-200" data-id="element-193" />}

                  <div className="relative z-10 w-5 h-5 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-194">
                    <div className="w-2 h-2 rounded-full bg-gray-400" data-id="element-195" />
                  </div>

                  <div data-id="element-196">
                    <p className="text-sm text-gray-900" data-id="element-197">
                      {activity.description}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-198">
                      {formatTimeAgo(activity.timestamp)}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 flex gap-3" data-id="element-199">
          <Button variant="primary" className="flex-1" disabled={isCancelled} data-id="element-200">
            Send to Five9 Now
          </Button>
          <Button variant="secondary" className="flex-1" data-id="element-201">
            Cancel Send
          </Button>
        </div>
      </div>
    </>;
}